import { Injectable, inject, NgZone } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { AuthService } from './auth.service';
import { ToastService } from 'src/utils/toast.service';
import { filter } from 'rxjs';
import { BaseModalService } from 'src/utils/base-modal.service';
import { ModalService } from 'src/utils/modal.service';
import { SessionStateService } from './session-state.service';
import { SessionParamsService } from './session-params.service';

@Injectable({
  providedIn: 'root'
})
export class TokenExpirationMonitorService {
  private authService = inject(AuthService);
  private router = inject(Router);
  private toastService = inject(ToastService);
  private baseModalService = inject(BaseModalService);
  private modalService = inject(ModalService);
  private sessionState = inject(SessionStateService);
  private sessionParamsService = inject(SessionParamsService);
  private ngZone = inject(NgZone);
  private expirationTimer?: number;
  private idleTimer?: number;
  private refreshScheduleTimer?: number;
  private isRefreshing = false;
  private bc = new BroadcastChannel('precredit_session_channel');
  private isHandlingExpiration = false;
  private tabId = Math.random().toString(36).substring(7);

  constructor() {
    this.bc.onmessage = (event) => {
      if (event.data.tabId === this.tabId) {
        return;
      }

      if (event.data.type === 'SESSION_EXPIRED') {
        this.handleTokenExpirationFromOtherTab();
      } else if (event.data.type === 'TOKEN_REFRESHED') {
        this.startMonitoring();
      }
    };

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        if (event.url.includes('/auth/login') || event.url.includes('/auth/')) {
          this.stopMonitoring();
        }
      });

    window.addEventListener('storage', (e) => {
      if (e.key === 'access_token') {
        if (e.newValue) {
          this.startMonitoring();
        } else {
          this.stopMonitoring();
        }
      }
    });
  }

  startMonitoring(): void {
    this.stopMonitoring();

    const token = this.authService.getAccessToken();
    if (!token) {
      return;
    }

    const expirationTime = this.authService.getSessionExpirationTime();
    if (!expirationTime) {
      return;
    }

    const now = Date.now();
    const timeUntilExpiration = expirationTime - now;

    if (timeUntilExpiration <= 0) {
      this.handleTokenExpiration();
      return;
    }

    this.expirationTimer = window.setTimeout(() => {
      this.handleTokenExpiration();
    }, timeUntilExpiration);

    this.scheduleProactiveRefresh();
    this.startIdleTimer();
  }

  stopMonitoring(): void {
    if (this.expirationTimer) {
      clearTimeout(this.expirationTimer);
      this.expirationTimer = undefined;
    }
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = undefined;
    }
    if (this.refreshScheduleTimer) {
      clearTimeout(this.refreshScheduleTimer);
      this.refreshScheduleTimer = undefined;
    }
  }

  startIdleTimer(): void {
    const idleTimeoutMs = this.sessionParamsService.getIdleTimeoutMinutes() * 60 * 1000;
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = undefined;
    }
    this.idleTimer = window.setTimeout(() => {
      this.handleIdleExpiration();
    }, idleTimeoutMs);
  }

  resetIdleTimer(): void {
    this.startIdleTimer();
  }

  private handleIdleExpiration(): void {
    if (this.isHandlingExpiration) {
      return;
    }
    this.isHandlingExpiration = true;
    this.sessionState.setAutoLogout(true);
    this.stopMonitoring();

    this.ngZone.run(() => {
      this.closeAllModals();
      this.bc.postMessage({ type: 'SESSION_EXPIRED', tabId: this.tabId });
      this.authService.clearTokensWithoutRevocation();

      this.router.navigate(['/auth/login'], {
        queryParams: { sessionExpired: 'idle' },
        replaceUrl: true
      }).then(() => {
        this.closeAllModals();
        this.resetHandlingState();
      }).catch(() => {
        this.resetHandlingState();
        window.location.href = '/auth/login?sessionExpired=idle';
      });
    });
  }

  public notifyTokenRefreshed(): void {
    this.bc.postMessage({ type: 'TOKEN_REFRESHED' });
    this.startMonitoring();
  }

  private handleTokenExpiration(): void {
    if (this.isHandlingExpiration) {
      return;
    }

    this.isHandlingExpiration = true;
    this.sessionState.setAutoLogout(true);
    this.stopMonitoring();

    this.ngZone.run(() => {
      this.closeAllModals();

      this.bc.postMessage({
        type: 'SESSION_EXPIRED',
        tabId: this.tabId
      });

      this.authService.clearTokensWithoutRevocation();

      this.router.navigateByUrl('/auth/login?sessionExpired=true', {
        replaceUrl: true,
        skipLocationChange: false
      }).then((success) => {
        this.closeAllModals();

        if (!success) {
          console.warn('Navigation failed, forcing reload');
          this.resetHandlingState();
          window.location.href = '/auth/login?sessionExpired=true';
          return;
        }

        this.resetHandlingState();

        setTimeout(() => {
          this.toastService.showToast(
            'fi fi-sr-triangle-warning text-[24px] text-[#F14A58]',
            'Tu sesión ha finalizado, por favor inicia sesión nuevamente.',
            'error',
            'right',
            300000
          );
        }, 150);
      }).catch((error) => {
        console.error('Error during auto-logout navigation:', error);
        this.resetHandlingState();
        window.location.href = '/auth/login?sessionExpired=true';
      });
    });
  }

  private closeAllModals(): void {
    this.baseModalService.closeAll();
    this.modalService.forceClose();

    Promise.resolve().then(() => {
      this.baseModalService.closeAll();
      this.modalService.forceClose();
    });
  }

  private resetHandlingState(): void {
    this.isHandlingExpiration = false;
  }

  private handleTokenExpirationFromOtherTab(): void {
    if (this.isHandlingExpiration) {
      return;
    }

    this.isHandlingExpiration = true;
    this.sessionState.setAutoLogout(true);
    this.stopMonitoring();

    this.closeAllModals();
    this.authService.clearTokensWithoutRevocation();

    this.router.navigate(['/auth/login'], {
      queryParams: { sessionExpired: 'true' }
    }).then(() => {
      this.closeAllModals();
      this.resetHandlingState();
    }).catch((error) => {
      console.error('Error during multi-tab logout navigation:', error);
      this.resetHandlingState();
      window.location.href = '/auth/login?sessionExpired=true';
    });
  }

  private scheduleProactiveRefresh(): void {
    if (this.refreshScheduleTimer) {
      clearTimeout(this.refreshScheduleTimer);
      this.refreshScheduleTimer = undefined;
    }

    const token = this.authService.getAccessToken();
    if (!token) return;

    const decoded = this.decodeJWT(token);
    if (!decoded?.exp) return;

    const expMs = decoded.exp * 1000;
    const refreshAt = expMs - (5 * 60 * 1000);
    const delay = refreshAt - Date.now();

    if (delay <= 0) {
      this.executeProactiveRefresh(0, expMs);
      return;
    }

    this.refreshScheduleTimer = window.setTimeout(() => {
      this.executeProactiveRefresh(0, expMs);
    }, delay);
  }

  private executeProactiveRefresh(attempt: number, windowEndMs: number): void {
    if (this.isRefreshing) return;

    const now = Date.now();
    if (now >= windowEndMs) return;

    this.isRefreshing = true;
    this.authService.proactiveRefresh().subscribe({
      next: () => {
        this.isRefreshing = false;
        this.notifyTokenRefreshed();
      },
      error: () => {
        this.isRefreshing = false;
        const retryDelay = 20000;
        const timeLeft = windowEndMs - Date.now();
        if (timeLeft > retryDelay) {
          this.refreshScheduleTimer = window.setTimeout(() => {
            this.executeProactiveRefresh(attempt + 1, windowEndMs);
          }, retryDelay);
        }
      }
    });
  }

  private decodeJWT(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      return JSON.parse(decodeURIComponent(
        window.atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
      ));
    } catch {
      return null;
    }
  }
}
